import Link from 'next/link'
import * as R from 'ramda'
import { useEffect, useState, useContext } from 'react'
import uuid from 'uuid/v1'

import CmsPageContext from 'contexts/cms-page-context'
import SurveyQuestion from 'components/shared/survey-question'
import useCurrentUser from 'hooks/use-current-user'
import { getAdjustedUtcOffset } from 'utilities/dates'
import ReengagementForm from 'components/shared/reengagement-form'
import { Button } from 'components/design-system/button'
import urls from 'utilities/urls'
import s from './styles.module.css'
import ArrowRight from './arrow-right'
import Image from 'next/image'

const FITNESS_GOAL_QUESTION_ID = 1
const FIRST_NAME_QUESTION_ID = 100
const EMAIL_QUESTION_ID = 132

const SignUpFormExperiment = ({
  surveyId,
  surveyQuestions,
  header,
  subHeader,
  coachProfileImageUrl,
  onSubmit,
  onContinue,
  isSubmitting,
  errorMessage,
  infoMessage,
  onCancelReengagement,
  isReengagement = false,
}) => {
  const { isCoachPage, selectedPriceId } = useContext(CmsPageContext)
  const currentUserPayload = useCurrentUser()
  const currentUser = R.pathOr(null, ['data', 'me', 'user'], currentUserPayload)
  const currentClient = R.propOr(null, 'client', currentUser)
  const firstName = R.propOr(null, 'firstName', currentUser)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)
  const [responses, setResponses] = useState({})
  const [changeResponses, setChangeResponses] = useState({})

  const showCurrentClient = !!currentClient && !isSubmitting

  useEffect(() => {
    setMessage(errorMessage)
  }, [errorMessage])

  useEffect(() => {
    setMessage(infoMessage)
  }, [infoMessage])

  const responsesBySurveyQuestionId = R.map(response => {
    if (response.surveyQuestionResponseIds) {
      return R.map(surveyQuestionResponseId => {
        return {
          ...response,
          surveyQuestionResponseId,
        }
      }, response.surveyQuestionResponseIds)
    }

    return [response]
  }, responses)
  const responseBySurveyQuestionId = R.map(R.head, responsesBySurveyQuestionId)

  const handleRespond = (questionId, response) => {
    if (!response) return setResponses(R.omit([questionId]))

    setResponses(R.assoc(questionId, { id: uuid(), ...response }))
  }

  const handleChangeResponse = (questionId, changeResponse) => {
    if (!changeResponse) return setChangeResponses(R.omit([questionId]))

    setChangeResponses(R.assoc(questionId, { id: uuid(), ...changeResponse }))
  }

  const isValid = R.all(({ id, isRequired: defaultIsRequired }) => {
    let isRequired = defaultIsRequired

    if (id === FIRST_NAME_QUESTION_ID && isCoachPage) {
      isRequired = true
    }

    if (!isRequired) return true

    if (id === EMAIL_QUESTION_ID && isCoachPage) return true

    const response = responses[id]
    const changeResponse = changeResponses[id]

    if (changeResponse && changeResponse.isValid) return true

    return (
      !!response &&
      (response.responseValue || response.surveyQuestionResponseIds)
    )
  }, surveyQuestions)

  const handleSubmit = ev => {
    ev && ev.preventDefault()

    if (isSubmitting) return
    if (!isValid) {
      return setError('Please provide first name and phone')
    }

    setError(null)

    const responseParams = R.reject(
      R.isNil,
      R.map(({ id, modelPath }) => {
        if (!changeResponses[id] && !responses[id]) {
          return null
        }

        const { responseValue, surveyQuestionResponseIds } = changeResponses[id]
          ? {
              responseValue: changeResponses[id].value,
            }
          : responses[id]

        return {
          surveyId,
          surveyQuestionId: id,
          modelPath,
          responseValue,
          surveyQuestionResponseIds,
        }
      }, surveyQuestions)
    )

    const fitnessGoalResponseIds = new Set(
      R.propOr(
        [],
        'surveyQuestionResponseIds',
        responseBySurveyQuestionId[FITNESS_GOAL_QUESTION_ID]
      )
    )
    const fitnessGoals = R.pipe(
      R.find(R.propEq('id', FITNESS_GOAL_QUESTION_ID)),
      R.propOr([], 'responses'),
      R.filter(({ id }) => fitnessGoalResponseIds.has(R.toString(id))),
      R.map(R.prop('text'))
    )(surveyQuestions)

    const utcOffset = getAdjustedUtcOffset()

    onSubmit({ utcOffset, fitnessGoals, responseParams })
  }

  let content

  if (showCurrentClient) {
    content = (
      <div className={s.form}>
        {firstName && <div className={s.label}>Welcome back, {firstName}!</div>}
        {!firstName && <div className={s.label}>Welcome back!</div>}
        {onContinue && (
          <Button
            className={s.submitButton}
            iconRight={<ArrowRight />}
            disabled={isSubmitting}
            onClick={onContinue}>
            Continue sign up
          </Button>
        )}
        {!onContinue && (
          <Link {...urls.clientSignUp()}>
            <Button
              className={s.submitButton}
              iconRight={<ArrowRight />}
              disabled={isSubmitting}>
              Continue sign up
            </Button>
          </Link>
        )}
      </div>
    )
  } else if (isReengagement) {
    content = (
      <div className={s.form}>
        <ReengagementForm
          onRetry={handleSubmit}
          onCancelReengagement={onCancelReengagement}
        />
      </div>
    )
  } else {
    content = (
      <form className={s.form} onSubmit={handleSubmit}>
        <div className={s.row}>
          <div>
            <label className={s.label}>What are your goals?</label>
            {R.map(question => {
              return (
                <SurveyQuestion
                  key={question.id}
                  respond={response => handleRespond(question.id, response)}
                  question={question}
                  responseBySurveyQuestionId={responseBySurveyQuestionId}
                  responsesBySurveyQuestionId={responsesBySurveyQuestionId}
                  inputClassName={s.input}
                  selectClassName={s.select}
                  selectOptionClassName={s.selectOption}
                  setOnChangeResponse={changeResponse => {
                    handleChangeResponse(question.id, changeResponse)
                  }}
                  hideHeader={true}
                  hideNote={true}
                  coachProfileVariant="C"
                />
              )
            }, R.slice(0, 1, surveyQuestions))}
          </div>
          <div>
            <label className={s.label}>What's your information?</label>
            {R.map(question => {
              return (
                <SurveyQuestion
                  key={question.id}
                  respond={response => handleRespond(question.id, response)}
                  question={question}
                  responseBySurveyQuestionId={responseBySurveyQuestionId}
                  responsesBySurveyQuestionId={responsesBySurveyQuestionId}
                  inputClassName={s.input}
                  selectClassName={s.select}
                  selectOptionClassName={s.selectOption}
                  setOnChangeResponse={changeResponse => {
                    handleChangeResponse(question.id, changeResponse)
                  }}
                  hideHeader={true}
                  hideNote={true}
                  coachProfileVariant="C"
                />
              )
            }, R.slice(1, Infinity, surveyQuestions))}
          </div>
        </div>
        <div className={s.row}>
          <div className={s.buttonWrapper}></div>
          <div className={s.buttonWrapper}>
            <Button
              className={s.submitButton}
              iconRight={<ArrowRight />}
              disabled={isSubmitting}
              type="submit">
              Get Started
            </Button>
          </div>
        </div>
        {error && <div className={s.error}>{error}</div>}
        {message && <div className={s.message}>{message}</div>}
      </form>
    )
  }

  return (
    <div className={s.formWrapper}>
      <div className={s.headline}>
        <h4 className={s.subHeader}>{subHeader}</h4>
        <h2 className={s.header}>{header}</h2>
      </div>
      <div className={s.headline}></div>
      <div className={s.coachImage}>
        <Image src={coachProfileImageUrl} layout="fill" objectFit="cover" />
      </div>
      {content}
    </div>
  )
}

export default SignUpFormExperiment
